import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Upload } from 'lucide-react';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { uploadMedia } from '@/lib/upload';

interface RenewalRequest {
  id: string;
  status: 'REQUESTED' | 'SUBMITTED' | 'CONFIRMED';
  dueDate: string;
  type: { name: string };
  child: { id: string; name: string };
}

/**
 * 서류 제출 — 연차 갱신 수합 (§5.22 P5, 학부모).
 * 원에서 요청한 서류를 사진으로 찍거나 파일로 올리면 선생님이 확인 후 편철한다.
 */
export function RenewalsPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [targetId, setTargetId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['documents', 'renewals', 'mine'],
    queryFn: () => apiData<RenewalRequest[]>('/documents/renewals/mine'),
  });

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const mediaId = await uploadMedia(file);
      await apiData(`/documents/renewals/${targetId}/submit`, {
        method: 'POST',
        body: { mediaId },
      });
      void queryClient.invalidateQueries({ queryKey: ['documents', 'renewals'] });
    } catch (caught) {
      setError(
        caught instanceof ApiError || caught instanceof Error ? caught.message : '제출하지 못했어요.',
      );
    } finally {
      setBusy(false);
    }
  };

  const rows = data ?? [];

  return (
    <Screen>
      <AppBar title="서류 제출" back />

      <div className="flex flex-col gap-3 p-4">
        {isPending && <Spinner />}
        {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

        {!isPending && rows.length === 0 && (
          <EmptyState
            title="제출할 서류가 없어요"
            body="원에서 서류를 요청하면 여기에서 사진으로 올릴 수 있어요."
          />
        )}

        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        {rows.map((row) => (
          <Card key={row.id} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-medium">
                {row.child.name} · {row.type.name}
              </p>
              <p className="text-sm text-ink-sub">{formatDate(row.dueDate)}까지</p>
            </div>
            {row.status === 'REQUESTED' && (
              <Button
                size="sm"
                icon={<Upload size={14} />}
                loading={busy && targetId === row.id}
                onClick={() => {
                  setTargetId(row.id);
                  fileRef.current?.click();
                }}
              >
                올리기
              </Button>
            )}
            {row.status === 'SUBMITTED' && <Chip tone="accent">확인 대기</Chip>}
            {row.status === 'CONFIRMED' && (
              <Chip tone="primary">
                <Check size={12} /> 완료
              </Chip>
            )}
          </Card>
        ))}
      </div>
    </Screen>
  );
}
